import { Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { CoreData, DashboardResolve, GarageContract, ProviderLinkOptions } from '@shared/models';
import { ActivatedRoute } from '@angular/router';
import { formatAddress, getContractPriceAdjustmentIncreaseString, TypedRoute } from '@shared/utils';
import { ArivoDialogComponent } from '@shared/components/ui-elements';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmplitudeEventService, CoreDataService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { LocalizedCurrencyPipe, LocalizedPercentagePipe } from '@core/pipes';
import { TranslateService } from '@ngx-translate/core';
import { AccessCodeShown, ContractCreateOpened, ContractEditOpened } from '@integrations/amplitude';

@Component({
  selector: 'app-dashboard-contracts',
  templateUrl: './dashboard-contracts.component.html',
  styleUrl: './dashboard-contracts.component.scss',
})
export class DashboardContractsComponent implements OnInit {
  @ViewChild('qrDialog') qrDialog!: ArivoDialogComponent;
  private destroyRef = inject(DestroyRef);
  qrCode: null | string = null;
  hasPaymentMethod = false;
  showPayPerUse = false;
  showContractReference = false;
  showPaymentMethodHints = false;
  creatingContractEnabled!: boolean;
  contractQrCodeEnabled!: boolean;
  showFreeflowGroupLimitWarning!: boolean;
  qrImageError = false;

  contracts: GarageContract[] = [];
  providerLinks!: ProviderLinkOptions;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<DashboardResolve>,
    private coreDataService: CoreDataService,
    protected featureFlagService: FeatureFlagService,
    private localizedCurrencyPipe: LocalizedCurrencyPipe,
    private localizedPercentagePipe: LocalizedPercentagePipe,
    private translateService: TranslateService,
    private providerSettingsService: ProviderSettingsService,
    private amplitudeEventService: AmplitudeEventService,
  ) {}

  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.contracts = this.sortContracts(data.dashboardApiResponse.garage_contracts);
      this.amplitudeEventService.updateUserProperties({ properties: { contractCount: this.contracts.length } });
    });
    this.coreDataService.coreData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((coreData: CoreData) => {
      this.hasPaymentMethod =
        coreData.signup_status.has_payment_method || this.coreDataService.isPaymentMethodOptional(coreData);
    });
    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });
    // Show payment method hints when external billing is NOT enabled (undefined/null treated as OFF)
    this.showPaymentMethodHints = this.featureFlagService.getFeatureFlag('enable_external_billing') !== true;
    this.showPayPerUse = this.featureFlagService.getFeatureFlag('enable_ppu') === true;
    this.showContractReference = this.featureFlagService.getFeatureFlag('enable_contract_reference') === true;
    this.creatingContractEnabled = this.featureFlagService.getFeatureFlag('enable_contract_creation_dashboard');
    this.contractQrCodeEnabled = this.featureFlagService.getFeatureFlag('enable_contract_qr_code');
    this.showFreeflowGroupLimitWarning = this.featureFlagService.getFeatureFlag('freeflow_with_group_limit');
  }

  sortContracts(contracts: GarageContract[]): GarageContract[] {
    return contracts.sort((a, b) => {
      const aHasNoLicensePlates = a.license_plates.length <= 0;
      const bHasNoLicensePlates = b.license_plates.length <= 0;
      const aIsActive = this.isContractActive(a);
      const bIsActive = this.isContractActive(b);

      if (aIsActive && bIsActive) {
        // Active contracts: prioritize those without license plates
        return Number(bHasNoLicensePlates) - Number(aHasNoLicensePlates);
      } else if (aIsActive !== bIsActive) {
        // Active contracts come before inactive contracts
        return Number(bIsActive) - Number(aIsActive);
      } else {
        // Both are inactive: no specific order
        return 0;
      }
    });
  }

  isRpcFallbackGarage(contract: GarageContract): boolean {
    if (!contract.garage) {
      return false;
    }
    return (
      !!contract.garage.name && !contract.garage.city && !contract.garage.street && !contract.garage.number && !contract.garage.zip_code
    );
  }

  openQrDialog(id: GarageContract['id']) {
    this.qrCode = this.contracts.find((contract) => contract.id === id)?.qr_src ?? null;
    if (this.qrCode) {
      this.amplitudeEventService.trackEvent(
        new AccessCodeShown({
          referredByPage: 'dashboard',
        }),
      );
      this.qrDialog.open = true;
      this.qrImageError = false;
    }
  }

  handleQrCodeError() {
    this.qrImageError = true;
  }

  dateIsInFuture(date: Date): boolean {
    return date > new Date();
  }

  getPriceIncreaseString(garageContract: GarageContract): string | null {
    return getContractPriceAdjustmentIncreaseString(
      garageContract,
      'Dashboard.Contracts.PriceIncreaseInfo',
      this.translateService,
      this.localizedCurrencyPipe,
      this.localizedPercentagePipe,
    );
  }

  get showVpi() {
    return this.featureFlagService.getFeatureFlag('enable_wipark_vpi');
  }

  isContractActive(contract: GarageContract): boolean {
    if (!contract.end) {
      return true;
    }
    
    const endDate = new Date(contract.end);
    endDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate >= today;
  }

  sendContractEditOpenedEventToAmplitude(): void {
    this.amplitudeEventService.trackEvent(
      new ContractEditOpened({
        referredByPage: 'dashboard',
      }),
    );
  }

  sendAddContractEventToAmplitude(): void {
    if (!this.hasPaymentMethod || !this.creatingContractEnabled) {
      return;
    }
    this.amplitudeEventService.trackEvent(
      new ContractCreateOpened({
        referredByPage: 'dashboard',
        contractType: 'contract',
      }),
    );
  }

  protected readonly formatAddress = formatAddress;
}
